/* eslint-disable react-hooks/exhaustive-deps */
import { QuestionCircleOutlined, SyncOutlined } from '@ant-design/icons'
import {
  Button,
  Checkbox,
  DatePicker,
  Flex,
  Form,
  FormInstance,
  InputNumber,
  notification,
  Radio,
  Select,
  Table,
  Tooltip,
  Typography,
} from 'antd/es'
import { useWatch } from 'antd/es/form/Form'
import TextArea from 'antd/es/input/TextArea'
import dayjs, { Dayjs } from 'dayjs'
import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { QUERY_KEYS } from '@api'
import { NoteElement, TicketFilesVariantDetail, TicketFilesVariantForm } from '@components'
import { CONTRACT_LINE_FIELD_NAME, ticketStatusToDisablePrimaryButtonforAdmin } from '@constants'
import { useUser } from '@contexts'
import {
  DeliveryNoteLabel,
  useCarriers,
  useDeliveryNotes,
  useDestinations,
  useEquipment,
  useMaterials,
  usePurchaseOrdersByMaterial,
  useSuppliers,
} from '@hooks'
import {
  AssignationValues,
  CarrierAvailabilitySummary,
  ComboOption,
  DeclinedLoadTypeEnum,
  Equipment,
  EquipmentDataSummarized,
  Note,
  RoleEnum,
  SapItemSupplier,
  StatusTicketEnum,
  Supplier,
  Ticket,
  TicketAssignmentType,
  TripTypeEnum,
} from '@types'
import {
  CutoffTimeUtils,
  DeliveryNoteUtils,
  filterPurchaseOrdersByTripType,
  OtherUtils,
  StringUtils,
} from '@utils'

import { CommentElement } from '../../../../components/CommentElement'
import StatusTicket from '../../../../components/StatusTicket'
import {
  FlexCol,
  FlexRow,
  StyledInput,
  StyledLabels,
  StyledRow,
  StyledSpinner,
} from '../../../../components/StyledComponents'
import { DATE_KEYS } from '../../../../constants/dateKeys'
import { useGetCarriersAvailabilitySummary } from '../../../../hooks/carrierAvailability/useGetCarriersAvailabilitySummary'
import { useGetContractsByCarrier } from '../../../../hooks/useGetContractsByCarrier'
import { theme } from '../../../../styles/theme'
import { mapDestinationsToCombo } from '../../../../utils/array-entity-to-combo'
import { limitIntEntry, preventEvent } from '../../../../utils/inputs'

const rowCarrierClassSetter = (status: StatusTicketEnum) => `row-${status}_CARRIER`
const { trimText, classesToString } = StringUtils

const { ADMIN, ADMINRO } = RoleEnum
const { PURCHASES } = QUERY_KEYS

const { PENDING, CONFIRMED, CREATED, TRANSIT, DELIVERED, CANCELED, NO_SHOW } = StatusTicketEnum
const statusRefs = [PENDING, CONFIRMED]
const permittedAdminEditionStatus = [PENDING, CREATED]
const ticketOwnedByCarrierStatus = [TRANSIT, DELIVERED]
const declinedLoadEditionStatus = [CONFIRMED, TRANSIT, DELIVERED]
const nonEditableStatus = [CANCELED, NO_SHOW, ...ticketOwnedByCarrierStatus]
const materialLockedStatuses = [CANCELED, NO_SHOW, DELIVERED]
const editablePurchaseOrderStatus = [CREATED, PENDING, CONFIRMED, TRANSIT]
const destinationEditableStatuses = [PENDING, CREATED, CONFIRMED, TRANSIT]

const handleFilterOptions = (input: string, option: any) => {
  if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
  return true
}

interface TicketAux {
  count: number
  supplierId?: number | undefined
  depotId?: number | undefined
  materialId?: number | undefined
  equipmentId?: number | undefined
  destinationId?: number | undefined
  ticketDate?: Date | Date[] | undefined
  carrierId?: number | undefined
  comments?: string | undefined
  approved?: boolean
  tripType?: TripTypeEnum
}

type Dates = Date[] | undefined

interface UpdateTicketFormAdminProps {
  form: FormInstance<any>
  handlePrimaryButtonDisabling: (value: boolean) => void
  editable: boolean | undefined
  setEditable: Dispatch<SetStateAction<boolean | undefined>>
  dates: Dates
  setDates: Dispatch<SetStateAction<Dates>>
  roles: RoleEnum[]
  isWarning: boolean | undefined
  setIsWarning: Dispatch<SetStateAction<boolean | undefined>>
  ifToApproved: boolean | undefined
  assignValue?: AssignationValues
  setAssignValue: Dispatch<SetStateAction<AssignationValues | undefined>>
  carrierAvailabilityIsEnabled: boolean
  ticket: Ticket
  maxAllowedDaysForSupplierRequest?: number
  isModalOpen: boolean
  cutoffTime: string
}

const UpdateTicketFormAdmin: FC<PropsWithChildren<UpdateTicketFormAdminProps>> = ({
  handlePrimaryButtonDisabling,
  editable,
  setEditable,
  ticket,
  dates,
  setDates,
  roles,
  isWarning,
  setIsWarning,
  ifToApproved,
  form,
  assignValue,
  setAssignValue,
  carrierAvailabilityIsEnabled,
  isModalOpen,
  cutoffTime,
}) => {
  // hooks
  const { t } = useTranslation()
  const deliveryNoteMinLength = DeliveryNoteUtils.DEFAULT_MIN_LENGTH
  const deliveryNoteMaxLength = DeliveryNoteUtils.DEFAULT_MAX_LENGTH
  const deliveryNoteValidationMessage = t('message_delivery_note_number', {
    min: deliveryNoteMinLength,
    max: deliveryNoteMaxLength,
  })
  const { state: user } = useUser()
  const queryClient = useQueryClient()
  const { allCarriersOptions } = useCarriers({ disabled: false, limit: 400 })
  const { equipments } = useEquipment()
  const { suppliersOptions, suppliers } = useSuppliers({ disabled: false })

  const carrierSelectOptions = useMemo(() => {
    const activeOptions = allCarriersOptions ?? []
    const currentId = ticket?.carrier?.id
    const alreadyIncluded = activeOptions.some(o => o.value === currentId)
    const carrierName = ticket?.carrier?.businessName || ticket?.carrier?.tradeName
    if (currentId && !alreadyIncluded && carrierName) {
      return [
        ...activeOptions,
        { label: `${carrierName} (${t('globals.generics.disabled')})`, value: currentId },
      ]
    }
    return activeOptions
  }, [allCarriersOptions, ticket?.carrier])

  const supplierSelectOptions = useMemo(() => {
    const activeOptions = suppliersOptions ?? []
    const currentId = ticket?.supplier?.id
    const alreadyIncluded = activeOptions.some(o => o.value === currentId)
    const supplierName = ticket?.supplier?.businessName || ticket?.supplier?.tradeName
    if (currentId && !alreadyIncluded && supplierName) {
      return [
        ...activeOptions,
        { label: `${supplierName} (${t('globals.generics.disabled')})`, value: currentId },
      ]
    }
    return activeOptions
  }, [suppliersOptions, ticket?.supplier])

  const {
    contractsByCarrier,
    getContractsByCarrier,
    getContractsByCarrierIsLoading,
    resetContractsByCarrier,
  } = useGetContractsByCarrier()

  // Status to control whether the simulation has been executed
  const [hasSimulationExecuted, setHasSimulationExecuted] = useState<boolean>(false)
  // Status to control whether manual simulation has been executed
  const [hasManualSimulationExecuted, setHasManualSimulationExecuted] = useState<boolean>(false)
  // states
  const [plantId, setPlantId] = useState<string>()
  const [depots, setDepots] = useState<ComboOption[]>([])
  const [isCalendarVisible, setIsCalendarVisible] = useState<boolean>(false)
  const [beforeToday, setBeforeToday] = useState<boolean>()
  const [required, setRequired] = useState<boolean>(true)
  const [, setAvailableQuantity] = useState<string>()
  const [initialValuesAux, setInitialValuesAux] = useState<TicketAux>()
  const [initialDate, setInitialDate] = useState<Date[]>() // form dependency
  const [selectedCarrier, setSelectedCarrier] = useState<number>()
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier>()
  const [showCarrierTable, setShowCarrierTable] = useState<boolean>()
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<SapItemSupplier>()
  const [enableDocument, setEnableDocument] = useState<boolean>(false)
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<number>()

  const currentContract = useWatch(CONTRACT_LINE_FIELD_NAME, form)

  const documentNumberId = useWatch('documentNumberId', form)
  const materialId = useWatch('materialId', form)
  const typedDeliveryNote = useWatch('number', form)
  const { purchaseOrders } = usePurchaseOrdersByMaterial(
    user?.userDetail.supplier?.sapId || selectedSupplier?.sapId,
    plantId || ticket?.destination?.plantId,
    ticket?.withdrawalDate ? new Date(ticket.withdrawalDate).toISOString() : undefined,
  )
  const { materials, materialsOptionsSapCode } = useMaterials(
    selectedPurchaseOrder?.DocumentNumberID || documentNumberId,
    undefined,
    ticket?.withdrawalDate ? new Date(ticket.withdrawalDate).toISOString() : undefined,
  )
  const currentDocumentNumberId = selectedPurchaseOrder?.DocumentNumberID || documentNumberId
  const {
    deliveryNotesOptions,
    isDeliveryNotesLoading,
    isSyncingDeliveryNotes,
    showDeliveryNoteNotFoundWarning,
    syncDeliveryNotes,
  } = useDeliveryNotes(currentDocumentNumberId, materialId, materials, {
    currentDeliveryNote:
      String(currentDocumentNumberId) === String(ticket.documentNumberId ?? '')
        ? ticket.deliveryNote
        : undefined,
    typedDeliveryNote,
  })

  const purchaseOrdersFilteredByPlant = useMemo(() => {
    const byPlant = plantId
      ? purchaseOrders?.filter(({ PlantID }) => PlantID === plantId)
      : purchaseOrders
    return filterPurchaseOrdersByTripType(
      byPlant,
      ticket.tripType,
      documentNumberId || ticket.documentNumberId,
    )
  }, [plantId, purchaseOrders, ticket.tripType, ticket.documentNumberId, documentNumberId])
  const currentSupplierSapId =
    selectedSupplier?.sapId || ticket?.supplier?.sapId || user?.userDetail.supplier?.sapId
  const { allDestinationsOptions, destinations } = useDestinations(currentSupplierSapId)
  const {
    carriersAvailabilitySummaryData,
    carriersAvailabilitySummaryDataIsLoading,
    refetchCarriersAvailabilitySummaryData,
  } = useGetCarriersAvailabilitySummary(
    dates ? dayjs(dates[0]).format(DATE_KEYS.FORMAT.ISO) : undefined,
  )

  const declinedLoadValue = useWatch('declinedLoad', form)
  const tripTypeValue = useWatch('tripType', form) as TripTypeEnum

  // Los campos Remito y Ticket SAP solo deben estar habilitados cuando el estado es CONFIRMED o TRANSIT
  // y la fecha de retiro NO es futura (ningún usuario puede modificar tickets con fecha futura)
  const canEditRemitoAndTicketSap = useMemo(() => {
    const status = ticket?.status.name
    const withdrawalDate = ticket?.withdrawalDate ? dayjs(ticket.withdrawalDate) : null
    // Habilitar solo si el estado es CONFIRMED o TRANSIT y la fecha NO es futura (comparando solo el día, no la hora)
    const isStatusValid = status === CONFIRMED || status === TRANSIT
    const isNotFutureDate = withdrawalDate
      ? withdrawalDate.startOf('day') <= dayjs().startOf('day')
      : false
    return isStatusValid && isNotFutureDate
  }, [ticket?.status.name, ticket?.withdrawalDate])

  // Determinar si el ticket es futuro (fecha mayor al día actual, sin importar la hora)
  const isFutureTicket = useMemo(() => {
    if (!ticket?.withdrawalDate) return false
    const withdrawalDate = dayjs(ticket.withdrawalDate)
    const today = dayjs().startOf('day')
    return withdrawalDate.startOf('day').isAfter(today)
  }, [ticket?.withdrawalDate])

  const isTicketReadOnly = roles?.includes(ADMINRO) ?? false

  const canEditDestination = useMemo(() => {
    if (isTicketReadOnly || !ticket?.status?.name) return false
    return destinationEditableStatuses.includes(ticket.status.name as StatusTicketEnum)
  }, [isTicketReadOnly, ticket?.status?.name])

  const isDeliveryNoteFieldEnabled =
    !isTicketReadOnly && canEditRemitoAndTicketSap && !!documentNumberId && !!materialId

  const isRequiredMsg = t('globals.obligatory_field')

  const closeCalendar = () => {
    setIsCalendarVisible(false)
  }

  const resetFields = () => {
    setInitialValuesAux(setInitialValues())
    setTimeout(form.resetFields, 1)
  }

  const resetPurchaseOrders = () => {
    form.resetFields(['depotId', 'materialId', 'destinationId'])
    form.setFieldsValue({ documentNumberId: undefined, destinationId: undefined })
    setPlantId(undefined)
    setSelectedPurchaseOrder(undefined)
    setAvailableQuantity(undefined)
    queryClient.removeQueries([PURCHASES])
  }

  const handleSelectCarrier = (carrierId: number | undefined) => {
    form.setFieldsValue({ carrierId })
  }

  const handleSelectEquipment = useCallback(
    (value?: number) => {
      setSelectedEquipmentId(value)
      if (initialValuesAux?.carrierId) {
        handlePrimaryButtonDisabling(!value)
      }
    },
    [initialValuesAux?.carrierId, handlePrimaryButtonDisabling],
  )

  const handleContractOnChange = (value: string) => {
    form.setFieldsValue({ [CONTRACT_LINE_FIELD_NAME]: value, sapItemId: undefined })
  }

  const editByCarrierIsBlocked = useMemo(
    () => ticketOwnedByCarrierStatus.includes(ticket?.status.name as StatusTicketEnum),
    [ticket?.status.name],
  )

  const truckInfo = useMemo(
    () =>
      OtherUtils.joinArrayValuesOnText([
        ticket?.truck?.licensePlate,
        ticket?.truck?.description,
        ticket?.truck?.equipmentType?.nameShort,
      ]),
    [ticket],
  )

  const trailerInfo = useMemo(
    () =>
      OtherUtils.joinArrayValuesOnText([
        ticket?.trailer?.licensePlate,
        ticket?.trailer?.description,
        ticket?.trailer?.equipmentType?.nameShort,
      ]),
    [ticket],
  )

  // Function to get contract data from ticketAssignments
  const getContractDataFromTicketAssignments = useCallback(
    (carrierId?: number) => {
      if (!ticket?.ticketAssignments || !carrierId) return null

      // Find the ticketAssignment for the specific carrier that has sapItem
      const assignmentWithSapItem = ticket.ticketAssignments.find(
        assignment => assignment.carrier.id === carrierId && assignment.sapItem,
      )

      return assignmentWithSapItem?.sapItem || null
    },
    [ticket?.ticketAssignments],
  )

  // Function to detect if fields were automatically selected for a specific carrier
  const wasAutoSelected = useCallback(
    (carrierId?: number) => {
      if (!carrierId || !ticket?.ticketAssignments) return false

      // Check if the specific carrier was automatically selected
      return ticket.ticketAssignments.some(
        assignment =>
          assignment.carrier.id === carrierId && assignment.autoAssign && assignment.sapItem,
      )
    },
    [ticket?.ticketAssignments],
  )

  // Function to get contract options based on ticket status and simulation state
  const getContractOptions = useCallback(() => {
    // Helper function to format contract label
    const formatContractLabel = (
      contract: string,
      descriptionContract?: string | null,
      companySap?: string | null,
    ) => {
      const companyPart = companySap ? ` (${companySap})` : ''
      if (descriptionContract) {
        return `${contract}${companyPart} - ${descriptionContract}`
      }
      return `${contract}${companyPart}`
    }

    // If manual simulation was run, ALWAYS prioritize simulation data
    if (hasManualSimulationExecuted && contractsByCarrier && contractsByCarrier.length > 0) {
      const contractOptions = contractsByCarrier.map(
        ({ contract, descriptionContract, companySap }) => ({
          label: formatContractLabel(contract, descriptionContract, companySap),
          value: contract,
        }),
      )
      return contractOptions
    }

    // For CONFIRMED tickets, use confirmed data from ticketAssignments
    if (ticket?.status.name === CONFIRMED && ticket?.ticketAssignments) {
      const confirmedContracts = [
        ...new Set(
          ticket.ticketAssignments
            .filter(assignment => assignment.sapItem && assignment.carrier.id === selectedCarrier)
            .map(assignment => assignment.sapItem.contract),
        ),
      ].map(contract => {
        // Try to find descriptionContract and companySap from contractsByCarrier
        const contractData = contractsByCarrier?.find(c => c.contract === contract)
        return {
          label: formatContractLabel(
            contract,
            contractData?.descriptionContract,
            contractData?.companySap,
          ),
          value: contract,
        }
      })

      // If there are confirmed contracts, return them
      if (confirmedContracts.length > 0) {
        return confirmedContracts
      }
    }

    // If it was auto-selected for this carrier, use data from ticketAssignments
    if (wasAutoSelected(selectedCarrier) && ticket?.ticketAssignments) {
      const autoSelectedContracts = [
        ...new Set(
          ticket.ticketAssignments
            .filter(assignment => assignment.sapItem && assignment.carrier.id === selectedCarrier)
            .map(assignment => assignment.sapItem.contract),
        ),
      ].map(contract => {
        // Try to find descriptionContract and companySap from contractsByCarrier
        const contractData = contractsByCarrier?.find(c => c.contract === contract)
        return {
          label: formatContractLabel(
            contract,
            contractData?.descriptionContract,
            contractData?.companySap,
          ),
          value: contract,
        }
      })

      if (autoSelectedContracts.length > 0) {
        return autoSelectedContracts
      }
    }

    // Default: use data from contractsByCarrier
    const defaultOptions =
      contractsByCarrier?.map(({ contract, descriptionContract, companySap }) => ({
        label: formatContractLabel(contract, descriptionContract, companySap),
        value: contract,
      })) || []
    return defaultOptions
  }, [
    ticket?.status.name,
    ticket?.ticketAssignments,
    selectedCarrier,
    hasManualSimulationExecuted,
    contractsByCarrier,
    wasAutoSelected,
  ])

  // Function to get sapItem options based on ticket status and simulation state
  const getSapItemOptions = useCallback(() => {
    // If manual simulation was run, ALWAYS prioritize simulation data
    if (hasManualSimulationExecuted && contractsByCarrier && contractsByCarrier.length > 0) {
      // Filter sapItems based on the currently selected contract
      const sapItemOptions =
        contractsByCarrier
          ?.find(({ contract }) => contract === currentContract)
          ?.contract_line.sort((a, b) => {
            // Sort by position first, then by subPosition
            if (a.position !== b.position) {
              return a.position - b.position
            }
            return a.subPosition - b.subPosition
          })
          .map(({ id, description, position, subPosition }) => ({
            label: `${position}/${subPosition} - ${description}`,
            value: id,
          })) || []

      return sapItemOptions
    }

    // For CONFIRMED tickets, use confirmed data from ticketAssignments
    if (ticket?.ticketAssignments) {
      const confirmedSapItems = ticket.ticketAssignments
        .filter(
          assignment =>
            assignment.sapItem &&
            assignment.carrier.id === selectedCarrier &&
            assignment.sapItem.contract === currentContract, // Filter by current contract
        )
        .sort((a, b) => {
          // Sort by position first, then by subPosition
          if (a.sapItem.position !== b.sapItem.position) {
            return a.sapItem.position - b.sapItem.position
          }
          return a.sapItem.subPosition - b.sapItem.subPosition
        })
        .map(assignment => ({
          label: `${assignment.sapItem.position}/${assignment.sapItem.subPosition} - ${assignment.sapItem.description}`,
          value: assignment.sapItem.id,
        }))

      // If there are confirmed sapItems for the current contract, return them
      if (confirmedSapItems.length > 0) {
        return confirmedSapItems
      }
    }

    // If it was auto-selected for this carrier, use data from ticketAssignments
    if (wasAutoSelected(selectedCarrier) && ticket?.ticketAssignments) {
      const autoSelectedSapItems = ticket.ticketAssignments
        .filter(
          assignment =>
            assignment.sapItem &&
            assignment.carrier.id === selectedCarrier &&
            assignment.sapItem.contract === currentContract, // Filter by current contract
        )
        .sort((a, b) => {
          // Sort by position first, then by subPosition
          if (a.sapItem.position !== b.sapItem.position) {
            return a.sapItem.position - b.sapItem.position
          }
          return a.sapItem.subPosition - b.sapItem.subPosition
        })
        .map(assignment => ({
          label: `${assignment.sapItem.position}/${assignment.sapItem.subPosition} - ${assignment.sapItem.description}`,
          value: assignment.sapItem.id,
        }))

      if (autoSelectedSapItems.length > 0) {
        return autoSelectedSapItems
      }
    }

    // Default: use data from contractsByCarrier based on selected contract
    const defaultOptions =
      contractsByCarrier
        ?.find(({ contract }) => contract === currentContract)
        ?.contract_line.sort((a, b) => {
          // Sort by position first, then by subPosition
          if (a.position !== b.position) {
            return a.position - b.position
          }
          return a.subPosition - b.subPosition
        })
        .map(({ id, description, position, subPosition }) => ({
          label: `${position}/${subPosition} - ${description}`,
          value: id,
        })) || []
    return defaultOptions
  }, [
    ticket?.status.name,
    ticket?.ticketAssignments,
    selectedCarrier,
    hasManualSimulationExecuted,
    contractsByCarrier,
    wasAutoSelected,
    currentContract,
  ])

  const setInitialValues = useCallback(() => {
    const contractData = getContractDataFromTicketAssignments(ticket?.carrier?.id)
    const isAutoSelected = wasAutoSelected(ticket?.carrier?.id)
    const isConfirmed = ticket?.status.name === CONFIRMED

    return {
      count: 1,
      supplierId: ticket?.supplier.id ?? undefined,
      depotId: ticket?.origin.id ?? undefined,
      materialId: ticket?.material?.idSap ?? undefined,
      equipmentId: ticket?.equipment ? ticket?.equipment.id : undefined,
      destinationId: ticket?.destination ? ticket?.destination.id : undefined,
      carrierId: ticket?.carrier ? ticket.carrier.id : undefined,
      comments: ticket?.comments ?? undefined,
      number: ticket?.deliveryNote,
      autoAssignValue: assignValue,
      approved: ticket?.status.name !== CREATED,
      documentNumberId: ticket?.documentNumberId,
      withdrawalTime: ticket?.withdrawalDate,
      declinedLoad: !!ticket?.declinedLoadAt,
      declinedLoadType: ticket?.declinedLoadType,
      ticketSap: ticket?.ticketSap,
      tripType: ticket?.tripType,
      // For CONFIRMED tickets, show confirmed data initially, but allow manual simulations later
      // For other states, use ticketAssignments data if auto-selected, otherwise use ticket.sapItem
      [CONTRACT_LINE_FIELD_NAME]:
        isConfirmed && contractData
          ? contractData.contract
          : isAutoSelected && contractData
          ? contractData.contract
          : ticket?.sapItem?.contract,
      sapItemId:
        isConfirmed && contractData
          ? contractData.id
          : isAutoSelected && contractData
          ? contractData.id
          : ticket?.sapItem?.id,
    }
  }, [ticket, assignValue, getContractDataFromTicketAssignments, wasAutoSelected])

  const handleDestinationSelect = useCallback(
    (value: number) => {
      handlePrimaryButtonDisabling(true)
      setAvailableQuantity(undefined)
      setSelectedPurchaseOrder(undefined)
      form.setFieldsValue({ materialId: undefined, documentNumberId: undefined, number: undefined })
      const destinationPlantId =
        destinations && destinations.find(destination => destination.id === value)
      const filteredPlantId = destinationPlantId?.plantId
      setPlantId(filteredPlantId)
    },
    [destinations, form, handlePrimaryButtonDisabling],
  )

  const isDateAfterToday = useCallback(
    () => dates && dates[0].getTime() < dayjs().toDate().getTime(),
    [dates],
  )

  const isTripCfr = useMemo(() => tripTypeValue === TripTypeEnum.CFR, [tripTypeValue])

  const calendarFooter = useCallback(
    () => (
      <FlexRow
        style={{
          justifyContent: 'end',
          padding: '0.5rem',
        }}
      >
        <Button type="primary" onClick={closeCalendar}>
          Aceptar
        </Button>
      </FlexRow>
    ),
    [roles],
  )

  const handleMaterialsOptions = useCallback(
    (value: number | undefined) => {
      const newSelectedMaterial = materials?.find(material => material.idSap === value)
      form.setFieldsValue({ material: newSelectedMaterial?.idSap, number: undefined })
      setAvailableQuantity(newSelectedMaterial?.sapItemSupplier?.AvailableQuantity ?? '')
      if (!materialLockedStatuses.includes(ticket?.status.name as StatusTicketEnum)) {
        handlePrimaryButtonDisabling(false)
      }
    },
    [materials, ticket?.status.name, form, handlePrimaryButtonDisabling],
  )

  const materialChange = useCallback(
    (value: number | undefined) => {
      if (value) {
        const newSelectedMaterial = materials?.find(material => material.idSap === value)
        if (newSelectedMaterial?.idSap) {
          form.setFieldsValue({ materialId: newSelectedMaterial.idSap })
        }
      }
      // Si value es undefined o no se encuentra el material, no cambiar el campo
    },
    [form, materials],
  )

  const handleDeliveryNoteChange = useCallback(
    (value: string | string[] | undefined) => {
      const normalized = DeliveryNoteUtils.normalizeValue(value)

      if (
        normalized &&
        !DeliveryNoteUtils.isValidDeliveryNote(
          normalized,
          deliveryNoteMinLength,
          deliveryNoteMaxLength,
        )
      ) {
        notification.error({
          message: deliveryNoteValidationMessage,
          key: 'materialError',
        })
        return
      }

      if (normalized) {
        const materialForSelectedDeliveryNote = materials?.filter(material =>
          material.sapItemSupplier?.sapItemSupplierDeliveryNotes?.some(
            item => item.deliveryNote === normalized,
          ),
        )[0]

        // Solo cambiar el material si se encuentra uno, de lo contrario mantener el actual
        if (materialForSelectedDeliveryNote?.idSap) {
          materialChange(materialForSelectedDeliveryNote.idSap)
        }
      }
      // Si se borra el remito o no se encuentra material, no cambiar el material actual
    },
    [
      materials,
      materialChange,
      deliveryNoteMinLength,
      deliveryNoteMaxLength,
      deliveryNoteValidationMessage,
    ],
  )
  const applyCutoffTime = useCallback(
    (value: string, inputDates?: Date[]) =>
      inputDates?.map(date => CutoffTimeUtils.applyCutoffTimeToDate(date, value)),
    [],
  )

  const handleTimeChange = () => {
    if (dates?.length && isDateAfterToday()) {
      setAssignValue('manual')
      setBeforeToday(true)
    } else {
      setBeforeToday(false)
    }
  }

  const handleChangeDate = useCallback(
    (thisDates?: Dayjs[]) => {
      const sorted = thisDates?.map(d => d.toDate()).sort((a, b) => (a < b ? -1 : 1))
      const datesWithCutoff = applyCutoffTime(cutoffTime, sorted)
      setDates(datesWithCutoff)

      if (thisDates) {
        form.setFieldsValue({ withdrawalDates: datesWithCutoff })
      }

      if (isTripCfr) {
        handlePrimaryButtonDisabling(false)
      }
    },
    [roles, tripTypeValue, isTripCfr, cutoffTime, applyCutoffTime],
  )

  const handleSupplierOptions = useCallback(
    (value: number | undefined) => {
      resetPurchaseOrders()
      if (value) {
        form.resetFields(['depotId', 'materialId', 'destinationId'])
        form.setFieldsValue({
          documentNumberId: undefined,
          destinationId: undefined,
          materialId: undefined,
          [CONTRACT_LINE_FIELD_NAME]: undefined,
          sapItemId: undefined,
        })
        setPlantId(undefined)
        resetContractsByCarrier()
        setHasSimulationExecuted(false)
        const supplierAux = suppliers?.find(supplier => supplier.id === value)
        setSelectedSupplier(supplierAux)
        const depotsAux = supplierAux?.depots.map(depot => ({
          label: depot.tag,
          value: depot.id,
        }))
        setDepots(depotsAux || [])
        setEnableDocument(!!supplierAux?.sapId)
      }
    },
    [suppliers, resetContractsByCarrier, setHasSimulationExecuted, form],
  )

  const clearDates = () => {
    setDates([])
    form.setFieldValue('withdrawalDates', [])
  }

  const handlePurchaseDocumentChange = useCallback(
    (selectedDocumentNumberId?: string) => {
      const order = purchaseOrders?.find(po => po.DocumentNumberID === selectedDocumentNumberId)
      if (!order) {
        form.setFieldsValue({
          documentNumberId: undefined,
          tripType: ticket.tripType,
          number: undefined,
        })
        setSelectedPurchaseOrder(undefined)
        setDates([])
        form.resetFields(['withdrawalDates'])
        return
      }

      setSelectedPurchaseOrder(order)
      setAvailableQuantity(undefined)
      form.setFieldsValue({
        documentNumberId: selectedDocumentNumberId,
        tripType: ticket.tripType,
        materialId: undefined,
        number: undefined,
      })

      handlePrimaryButtonDisabling(true)
    },
    [purchaseOrders, form, handlePrimaryButtonDisabling, setDates, ticket.tripType],
  )

  const TOOLTIP_TEXTS: Record<TripTypeEnum, string> = {
    [TripTypeEnum.FOB]: t('selected_Incoterm.fob'),
    [TripTypeEnum.CFR]: t('selected_Incoterm.cfr'),
    [TripTypeEnum.TRANSFERENCE]: '',
  }

  const getTooltipText = (tripType?: TripTypeEnum) =>
    TOOLTIP_TEXTS[tripType || TripTypeEnum.TRANSFERENCE] || ''

  const TripTypeDisplay: React.FC<{ tripType?: TripTypeEnum }> = ({ tripType }) => {
    const displayValue = tripType ? t(`trip_type.${tripType}`) : ''
    return (
      <Tooltip title={getTooltipText(tripType)}>
        <span className="ticket-field-value">{displayValue}</span>
      </Tooltip>
    )
  }

  // Function to obtain automatic simulation data (from table)
  const handleSearchContractPosition = useCallback(
    ({ carrierId, autoSelect }: { carrierId: number | undefined; autoSelect?: boolean }) => {
      if (!carrierId) {
        notification.error({
          message: t('globals.generic_error'),
          description: 'Faltan datos requeridos para obtener los contratos',
        })
        return
      }

      // Check if there is contract data in ticketAssignments for this carrier
      const contractDataFromAssignments = getContractDataFromTicketAssignments(carrierId)
      const isAutoSelected = wasAutoSelected(carrierId)

      // If it was auto-selected and there is data in ticketAssignments, use that data
      if (isAutoSelected && contractDataFromAssignments) {
        // Only set hasSimulationExecuted if this is an auto-select operation
        if (autoSelect) {
          setHasSimulationExecuted(true)
        }
        form.setFieldsValue({
          [CONTRACT_LINE_FIELD_NAME]: contractDataFromAssignments.contract,
          sapItemId: contractDataFromAssignments.id,
        })
        return
      }

      // If there is no data in ticketAssignments or it was not auto-selected, use the API
      // Include sapItemId if there's an existing contract assignment to show it even if disabled
      const existingSapItemId = contractDataFromAssignments?.id
      getContractsByCarrier(
        { carrierId, ...(existingSapItemId && { sapItemId: existingSapItemId }) },
        {
          onSuccess: data => {
            // Only mark that the simulation has been executed if this is an auto-select operation
            if (autoSelect) {
              setHasSimulationExecuted(true)
            }
            // Only apply dark gray color if simulation was successful and returned contracts
            // If it returns an empty array, keep the original color (gray)
            if (data && data.length > 0) {
              // Successful simulation with contracts - apply dark gray color)
              // TRANSFERENCE: never bind the first contract line; the user must pick it.
              if (autoSelect && tripTypeValue !== TripTypeEnum.TRANSFERENCE) {
                form.setFieldsValue({
                  [CONTRACT_LINE_FIELD_NAME]: data?.[0]?.contract,
                  sapItemId: data?.[0]?.contract_line?.[0]?.id,
                })
              }
            } else {
              // Simulation failed (empty array) - keep original color (gray)
              if (autoSelect) {
                setHasSimulationExecuted(false)
              }
            }
          },
        },
      )
    },
    [form, getContractDataFromTicketAssignments, wasAutoSelected, tripTypeValue],
  )

  // Function for manual simulation (from button) - always calls the service
  const handleManualSimulation = useCallback(
    ({ carrierId }: { carrierId: number | undefined }) => {
      if (!carrierId) {
        notification.error({
          message: t('globals.generic_error'),
          description: 'Faltan datos requeridos para obtener los contratos',
        })
        return
      }

      // Include sapItemId if there's an existing contract assignment to show it even if disabled
      const existingSapItemId = getContractDataFromTicketAssignments(carrierId)?.id

      // Always call the contracts service for manual simulation
      getContractsByCarrier(
        { carrierId, ...(existingSapItemId && { sapItemId: existingSapItemId }) },
        {
          onSuccess: () => {
            // Mark that manual simulation has been executed
            setHasManualSimulationExecuted(true)
            // Always mark that the simulation has been executed for manual simulation
            setHasSimulationExecuted(true)
          },
        },
      )
    },
    [getContractsByCarrier, getContractDataFromTicketAssignments],
  )

  useEffect(() => {
    if (isTripCfr !== undefined) {
      setAssignValue(isTripCfr ? 'manual' : assignValue)
    }
  }, [isTripCfr, assignValue])

  useEffect(() => {
    if (initialValuesAux) {
      if (roles?.includes(ADMINRO)) {
        setShowCarrierTable(true)
      } else if (!tripTypeValue || isTripCfr) {
        setShowCarrierTable(false)
      } else if (beforeToday !== undefined) {
        setShowCarrierTable(
          !beforeToday && !!carrierAvailabilityIsEnabled && !editByCarrierIsBlocked,
        )
      } else {
        setShowCarrierTable(false)
      }
    }
  }, [
    initialValuesAux,
    beforeToday,
    carrierAvailabilityIsEnabled,
    editByCarrierIsBlocked,
    tripTypeValue,
    isTripCfr,
    roles,
  ])

  useEffect(() => {
    if (ticket?.withdrawalDate) {
      setInitialDate([dayjs(ticket.withdrawalDate).toDate()])
    } else {
      setInitialDate(undefined)
    }
  }, [ticket])

  useEffect(() => {
    if (initialDate && !dates) {
      setDates(initialDate)
    }
  }, [initialDate])

  useEffect(() => {
    if (!initialValuesAux && assignValue && setInitialValues) {
      setInitialValuesAux(setInitialValues())
    }
  }, [setInitialValues, assignValue, initialValuesAux])

  useEffect(() => {
    if (ticket.equipment) setSelectedEquipmentId(ticket.equipment.id)
    if (!window.location.pathname.includes('/evouchers')) {
      setRequired(true)
    } else {
      setRequired(false)
    }
    handleSupplierOptions(ticket?.supplier.id)

    if (ticket.destination) {
      setPlantId(ticket.destination.plantId || undefined)
    }

    const withdrawalDate = dayjs(ticket.withdrawalDate)

    // Ningún usuario (incluido Admin) puede editar tickets con fecha futura (comparando solo el día, no la hora)
    const isFutureDate = withdrawalDate.startOf('day') > dayjs().startOf('day')

    if (
      roles?.includes(ADMIN) &&
      permittedAdminEditionStatus.includes(ticket.status.name as StatusTicketEnum) &&
      !isFutureDate &&
      !nonEditableStatus.includes(ticket.status.name as StatusTicketEnum)
    ) {
      setEditable(true)
      handlePrimaryButtonDisabling(false)
    } else {
      setEditable(false)
      handlePrimaryButtonDisabling(true)
    }

    if (ticket.status.name === DELIVERED || ticket.status.name === CANCELED) {
      setEditable(false)
      setEnableDocument(false)
      handlePrimaryButtonDisabling(true)
    }

    if (ticket.status.name === NO_SHOW) {
      setEditable(false)
      setEnableDocument(false)
      handlePrimaryButtonDisabling(true)
    }

    if (
      roles?.includes(ADMIN) &&
      ticket.status.name !== DELIVERED &&
      ticket.status.name !== CANCELED &&
      ticket.status.name !== NO_SHOW
    ) {
      setEnableDocument(ticket.tripType === TripTypeEnum.FOB)
      handlePrimaryButtonDisabling(false)
    }

    if (ticket.carrier?.id) {
      // Include sapItemId if there's an existing contract assignment to show it even if disabled
      const existingSapItemId = getContractDataFromTicketAssignments(ticket.carrier.id)?.id
      getContractsByCarrier({
        carrierId: ticket.carrier.id,
        ...(existingSapItemId && { sapItemId: existingSapItemId }),
      })
      setSelectedCarrier(ticket.carrier.id)
      handleSearchContractPosition({ carrierId: ticket.carrier.id, autoSelect: false })
    } else {
      setSelectedCarrier(undefined)
    }
    resetFields()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen])

  // Reset simulation state for CONFIRMED tickets to show confirmed data initially
  useEffect(() => {
    if (ticket?.status.name === CONFIRMED) {
      setHasSimulationExecuted(false)
    }
  }, [ticket?.status.name])

  useEffect(() => {
    if (selectedCarrier) {
      const selectedCarrierElement = document.querySelector(
        '.ant-table-row-selected',
      ) as HTMLElement
      if (selectedCarrierElement) {
        selectedCarrierElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
    handleSelectCarrier(selectedCarrier)
  }, [selectedCarrier])

  useEffect(() => {
    if (ticket && roles.includes(ADMIN) && !ticket.carrier?.id) {
      if (assignValue === 'automatic') {
        setSelectedCarrier(undefined)
      }
    }
  }, [assignValue])

  useEffect(() => {
    if (!!dates?.length) {
      refetchCarriersAvailabilitySummaryData()
      setIsWarning(false)
      handleTimeChange()

      form.setFieldsValue({
        withdrawalDates: dates,
      })
    }
  }, [dates, cutoffTime]) //, ticket?.withdrawalDate, form

  useEffect(() => {
    if (selectedPurchaseOrder) {
      form.setFieldsValue({ materialId: undefined, number: undefined })
      form.setFieldsValue({ tripType: ticket.tripType })
    }
  }, [selectedPurchaseOrder, form, ticket.tripType])

  useEffect(() => {
    if (ticket.tripType) {
      form.setFieldsValue({ tripType: ticket.tripType })
      return
    }
    if (roles?.includes(ADMIN) && !selectedSupplier?.sapId) {
      form.setFieldsValue({ tripType: TripTypeEnum.TRANSFERENCE })
    } else {
      form.setFieldsValue({ tripType: undefined })
    }
  }, [selectedSupplier, roles, form, ticket.tripType])

  useEffect(() => {
    if (tripTypeValue === TripTypeEnum.CFR) {
      const currentValue = form.getFieldValue('count')
      if (currentValue !== 1) {
        form.setFieldsValue({ count: 1 })
      }

      if (dates && dates.length > 1) {
        const singleDate = [dates[0]]
        setDates(singleDate)
        form.setFieldsValue({
          withdrawalDates: singleDate,
        })
      }
    }
  }, [tripTypeValue, form, dates])

  const depotId = useWatch('depotId', form)
  const destinationId = useWatch('destinationId', form)

  const clearContractFields = useCallback(() => {
    resetContractsByCarrier()
    setHasSimulationExecuted(false)
    // Clear form fields
    form.setFieldsValue({
      [CONTRACT_LINE_FIELD_NAME]: null,
      sapItemId: null,
    })
  }, [resetContractsByCarrier, form, setHasSimulationExecuted])

  // Clear simulation state when carrier changes
  const clearSimulationOnCarrierChange = useCallback(() => {
    setHasSimulationExecuted(false)
  }, [])

  useEffect(() => {
    if (showCarrierTable !== undefined && !isTripCfr && initialValuesAux) {
      // Clear contract fields when changing carrier (any change)
      // Also clean when changing depot or destination
      if (depotId !== ticket.origin?.id || destinationId !== ticket.destination?.id) {
        clearContractFields()
      }
    }
  }, [depotId, destinationId])

  return (
    <Form
      form={form}
      initialValues={initialValuesAux}
      name="ticket-form"
      disabled={isTicketReadOnly}
      className={`ticket-form ${hasSimulationExecuted ? 'simulation-executed' : ''}`}
    >
      <FlexCol className="ticket-form__column ticket-data">
        {!!(ticket && !editable && ticket.status.name) && (
          <StatusTicket status={ticket.status.name}>
            {(ticket.status.name === 'CONFIRMED' || ticket.status.name === 'TRANSIT') && (
              <span style={{ marginLeft: 'auto' }}>
                <Tooltip
                  title={t(
                    'notifications.the_trip_will_be_delivered_when_the_sap_ticket_is_validated.',
                  )}
                  style={{ marginLeft: 'auto' }}
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            )}
          </StatusTicket>
        )}
        <FlexCol className="form-section">
          {ticket && !editable && (
            <StyledRow className="form-detail-title">
              <span>{`${t('Detalle ID')} #${ticket?.id}`}</span>
            </StyledRow>
          )}
          {!ticket && (
            <StyledRow className="new-ticket-form-row">
              <Form.Item
                label={t('suppliers_router.new_ticket_modal.quantity')}
                name="count"
                initialValue={tripTypeValue === TripTypeEnum.CFR ? 1 : undefined}
                rules={[
                  {
                    required: true,
                    message: isRequiredMsg,
                  },
                  ...(tripTypeValue === TripTypeEnum.CFR
                    ? [
                        {
                          validator: (_: unknown, value: number) =>
                            value === 1
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error(
                                    t('suppliers_router.new_ticket_modal.cfr_quantity_error'),
                                  ),
                                ),
                        },
                      ]
                    : []),
                ]}
              >
                <>
                  <InputNumber
                    onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => preventEvent(event)}
                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) =>
                      limitIntEntry(event)
                    }
                    min={1}
                    max={10}
                    disabled={tripTypeValue === TripTypeEnum.CFR}
                    style={{ width: '100%' }}
                    value={form.getFieldValue('count')}
                  />
                </>
              </Form.Item>
            </StyledRow>
          )}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label="Proveedor"
              name="supplierId"
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
              ]}
            >
              <Select
                allowClear
                showSearch
                disabled
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder={t('globals.placeholder_select')}
                options={supplierSelectOptions}
                onChange={handleSupplierOptions}
                filterOption={handleFilterOptions}
              />
            </Form.Item>
          </StyledRow>
          {/* ORIGEN */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label="Origen"
              name="depotId"
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
              ]}
            >
              <Select
                showSearch
                filterOption={handleFilterOptions}
                disabled
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder={t('globals.placeholder_select')}
                options={depots}
                onChange={(value: number) => {
                  form.setFieldValue('depotId', value)
                }}
              />
            </Form.Item>
          </StyledRow>
          {/* DESTINO */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label="Destino"
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
              ]}
              name="destinationId"
            >
              <Select
                showSearch
                filterOption={handleFilterOptions}
                allowClear
                disabled={ticket ? !canEditDestination : false}
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder={t('globals.placeholder_select')}
                options={
                  currentSupplierSapId
                    ? mapDestinationsToCombo(destinations)
                    : allDestinationsOptions
                }
                onChange={handleDestinationSelect}
              />
            </Form.Item>
          </StyledRow>
          {/* ORDEN DE COMPRA */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('purchase_order')}
              name="documentNumberId"
              rules={[
                {
                  required: enableDocument,
                  message: isRequiredMsg,
                },
              ]}
            >
              <Select
                showSearch
                filterOption={handleFilterOptions}
                disabled={
                  ticket
                    ? isTicketReadOnly ||
                      !editablePurchaseOrderStatus.includes(
                        ticket.status.name as StatusTicketEnum,
                      ) ||
                      ticket.status.name === DELIVERED ||
                      ticket.status.name === CANCELED ||
                      ticket.status.name === NO_SHOW
                    : !tripTypeValue && (!enableDocument || !plantId)
                }
                placeholder={!!selectedSupplier?.sapId && t('globals.placeholder_select')}
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                options={purchaseOrdersFilteredByPlant?.map(
                  ({ DocumentNumberID, ProposedDeliveryDate, Inco1, Inco2 }) => ({
                    label: `${DocumentNumberID} - ${OtherUtils.capitalize(
                      dayjs(ProposedDeliveryDate).format('MMM-YY'),
                    )} - ${Inco1}/${Inco2}`,
                    value: DocumentNumberID,
                  }),
                )}
                onChange={handlePurchaseDocumentChange}
              />
            </Form.Item>
          </StyledRow>
          {/* TIPO VIAJE */}
          {tripTypeValue === TripTypeEnum.TRANSFERENCE ? (
            <StyledRow className="new-ticket-form-row">
              <Form.Item label="Incoterm" name="tripType">
                <TripTypeDisplay tripType={TripTypeEnum.TRANSFERENCE} />
              </Form.Item>
            </StyledRow>
          ) : (
            <StyledRow className="new-ticket-form-row">
              <Form.Item
                label="Incoterm"
                rules={[{ required, message: isRequiredMsg }]}
                name="tripType"
              >
                <TripTypeDisplay tripType={tripTypeValue} />
              </Form.Item>
            </StyledRow>
          )}
          {/* MATERIAL */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label="Material"
              name="materialId"
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
                {
                  validator: () => {
                    if (ticket?.status.name === 'DELIVERED') {
                      return Promise.reject(
                        notification.info({
                          message: t('tpl_dashboard.delivered_error'),
                          key: 'materialError',
                        }),
                      )
                    }
                    return Promise.resolve()
                  },
                },
              ]}
            >
              <Select
                showSearch
                onChange={handleMaterialsOptions}
                filterOption={handleFilterOptions}
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder={t('globals.placeholder_select')}
                options={materialsOptionsSapCode}
                disabled={
                  isTicketReadOnly ||
                  materialLockedStatuses.includes(ticket?.status?.name as StatusTicketEnum)
                }
              />
            </Form.Item>
          </StyledRow>
          {/* EQUIPO */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label="Equipo"
              rules={[
                {
                  required: !!roles?.includes(ADMIN) && required,
                  message: isRequiredMsg,
                },
              ]}
              name="equipmentId"
            >
              <Select
                showSearch
                filterOption={handleFilterOptions}
                allowClear
                disabled={
                  !(
                    roles?.includes(ADMIN) &&
                    ticket &&
                    !nonEditableStatus.includes(ticket.status.name as StatusTicketEnum)
                  )
                }
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder={t('globals.placeholder_select')}
                options={equipments?.map(equipment => ({
                  label: equipment.nameShort,
                  value: equipment.id,
                }))}
                onChange={handleSelectEquipment}
              />
            </Form.Item>
          </StyledRow>
          {/* FECHA */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={
                <>
                  {required && <div className="required-asterisk">*</div>}
                  <span>Fecha</span>
                </>
              }
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
              ]}
            >
              <FlexCol className="ticket-form__dates-column">
                <FlexRow>
                  {ticket && !editable ? (
                    <DatePicker
                      className="ticket-form__date-picker-viewer"
                      disabled
                      showTime={{ format: DATE_KEYS.FORMAT.TIME }}
                      format={DATE_KEYS.FORMAT.DAY_SHORT}
                      value={dayjs(ticket?.withdrawalDate)}
                    />
                  ) : (
                    <DatePicker
                      multiple={!ticket && tripTypeValue !== TripTypeEnum.CFR}
                      onChange={date => {
                        if (!date) {
                          clearDates()
                          return
                        }
                        const thisDates = Array.isArray(date) ? date : [date]
                        handleChangeDate(thisDates as Dayjs[])
                      }}
                      open={isCalendarVisible}
                      renderExtraFooter={calendarFooter}
                      onClick={() => {
                        setIsCalendarVisible(true)
                      }}
                      value={
                        !ticket
                          ? dates?.map(date => dayjs(date))
                          : dates?.length
                          ? dayjs(dates[0])
                          : undefined
                      }
                      maxTagCount="responsive"
                      required
                      allowClear
                      format={DATE_KEYS.FORMAT.DAY_SHORT}
                      className={classesToString([
                        'ticket-form__date-picker-input',
                        isWarning && 'ticket-form__is-warning',
                      ])}
                      disabled={ticket && !editable}
                    />
                  )}
                </FlexRow>
                <FlexRow>
                  {!!dates?.length
                    ? `${t('suppliers_router.new_ticket_modal.dates_quantity')} ${dates?.length}`
                    : ''}
                </FlexRow>
              </FlexCol>
            </Form.Item>
          </StyledRow>
          {/* HORARIO DE CORTE */}
          {!isTripCfr && (
            <StyledRow className="new-ticket-form-row">
              <Form.Item
                label={`${t('globals.cutoff_time')}:`}
                className="cutoff-time-form-item"
                colon={false}
              >
                <span className={classesToString([isWarning && 'ticket-form__is-warning'])}>
                  {t('globals.cutoff_time_info_short', { time: cutoffTime })}
                </span>
              </Form.Item>
            </StyledRow>
          )}
          {/* TODO: Carrier Select */}
          {!showCarrierTable &&
            assignValue === 'manual' &&
            (roles?.includes(ADMIN) || roles?.includes(ADMINRO)) &&
            (ticket?.ticketAssignments.length || ticket?.status.name === CREATED) && (
              <StyledRow className="new-ticket-form-row">
                <Form.Item
                  label="Transportista"
                  name="carrierId"
                  rules={
                    tripTypeValue !== TripTypeEnum.CFR
                      ? [
                          {
                            required: roles?.includes(ADMIN) && ticket?.status.name === CREATED,
                            message: isRequiredMsg,
                          },
                        ]
                      : []
                  }
                >
                  <Select
                    disabled={
                      roles?.includes(ADMINRO) ||
                      ![PENDING, CONFIRMED, CREATED].includes(
                        ticket?.status?.name as StatusTicketEnum,
                      ) ||
                      isTripCfr
                    }
                    onChange={(value: number) => {
                      clearSimulationOnCarrierChange()
                      setSelectedCarrier(value)
                    }}
                    getPopupContainer={(triggerNode: HTMLElement) =>
                      triggerNode.parentNode as HTMLElement
                    }
                    placeholder={t('globals.placeholder_select')}
                    showSearch
                    filterOption={handleFilterOptions}
                    options={carrierSelectOptions.map(option => ({
                      label: option.label || 'N/A',
                      value: option.value,
                    }))}
                    allowClear
                  />
                </Form.Item>
              </StyledRow>
            )}
          {/* ASIGNACION */}
          {roles?.includes(ADMIN) &&
            ((!!ifToApproved && ticket?.ticketAssignments.length === 0) || !ticket) && (
              <StyledRow className="new-ticket-form-row" id="radio-button">
                <Form.Item label="Asignación" name="autoAssignValue">
                  <FlexRow className="ticket-form__assign-radio-group">
                    {['automatic', 'manual'].map(assignation => {
                      const disabled =
                        isTripCfr || (assignation === 'automatic' ? beforeToday : undefined)

                      return (
                        <FlexRow className="ticket-form__assign-radio-input" key={assignation}>
                          <Radio
                            value={assignation}
                            disabled={disabled}
                            onChange={() =>
                              !isTripCfr && setAssignValue(assignation as 'automatic' | 'manual')
                            }
                            checked={
                              isTripCfr ? assignation === 'manual' : assignValue === assignation
                            }
                          />
                          <StyledLabels>{t(`globals.generics.${assignation}`)}</StyledLabels>
                        </FlexRow>
                      )
                    })}
                  </FlexRow>
                </Form.Item>
              </StyledRow>
            )}
          {/* CAMPOS DE CONTRATO */}
          {(() => {
            const isAdminOrAdminRO =
              roles?.includes(RoleEnum.ADMIN) || roles?.includes(RoleEnum.ADMINRO)
            const isFOB = tripTypeValue === TripTypeEnum.FOB
            const isTransference = tripTypeValue === TripTypeEnum.TRANSFERENCE
            const shouldShowContractFields = isAdminOrAdminRO && (isFOB || isTransference)

            return shouldShowContractFields
          })() && (
            <>
              <StyledRow className="new-ticket-form-row">
                <FlexRow style={{ alignItems: 'start', gap: '8px', flex: 1 }}>
                  <Form.Item
                    label={t('admin_router.admin_dashboard.settings.contract')}
                    name={CONTRACT_LINE_FIELD_NAME}
                    rules={[
                      {
                        required: tripTypeValue === TripTypeEnum.FOB,
                        message: isRequiredMsg,
                      },
                    ]}
                  >
                    <Select
                      key={`contract-select-${hasManualSimulationExecuted}-${
                        contractsByCarrier?.length || 0
                      }-${Date.now()}`}
                      placeholder={t('globals.placeholder_select')}
                      options={getContractOptions()}
                      onChange={handleContractOnChange}
                      disabled={
                        isTicketReadOnly ||
                        ticketOwnedByCarrierStatus.includes(ticket?.status.name as StatusTicketEnum)
                      }
                      value={undefined}
                    />
                  </Form.Item>
                  <Tooltip
                    title={t('admin_router.admin_dashboard.settings.search_contract_position')}
                  >
                    <Button
                      type="primary"
                      icon={<SyncOutlined spin={getContractsByCarrierIsLoading} />}
                      onClick={() => {
                        handleManualSimulation({ carrierId: selectedCarrier })
                      }}
                      disabled={
                        roles?.includes(RoleEnum.ADMINRO) ||
                        !selectedCarrier ||
                        !selectedEquipmentId ||
                        !dates?.length ||
                        getContractsByCarrierIsLoading ||
                        ticketOwnedByCarrierStatus.includes(ticket?.status.name as StatusTicketEnum)
                      }
                      loading={getContractsByCarrierIsLoading}
                      className="contract-search-button"
                    />
                  </Tooltip>
                </FlexRow>
              </StyledRow>
              {/* TODO: CAMPOS DE CONTRATO AGREGADO */}
              <StyledRow className="new-ticket-form-row">
                <Form.Item
                  label={t('admin_router.admin_dashboard.settings.contract_line')}
                  name="sapItemId"
                  rules={[
                    {
                      required: tripTypeValue === TripTypeEnum.FOB,
                      message: isRequiredMsg,
                    },
                  ]}
                >
                  <Select
                    key={`sapitem-select-${hasManualSimulationExecuted}-${
                      contractsByCarrier?.length || 0
                    }-${Date.now()}`}
                    style={{ flex: 1 }}
                    showSearch
                    placeholder={t('globals.placeholder_select')}
                    options={getSapItemOptions()}
                    disabled={
                      !roles?.includes(RoleEnum.ADMIN) ||
                      ticketOwnedByCarrierStatus.includes(ticket?.status.name as StatusTicketEnum)
                    }
                    filterOption={handleFilterOptions}
                    getPopupContainer={(triggerNode: HTMLElement) =>
                      triggerNode.parentNode as HTMLElement
                    }
                    value={undefined}
                  />
                </Form.Item>
              </StyledRow>
              {/* TODO: CAMPOS DE CONTRATO AGREGADO */}
            </>
          )}
          <StyledRow className="new-ticket-form-row">
            <Form.Item label={t('ticket_modal.created_at')} name="createAt">
              <span className="ticket-field-value">
                {dayjs(ticket?.createAt).format(DATE_KEYS.FORMAT.FULL_TIME)}
              </span>
            </Form.Item>
          </StyledRow>
          <StyledRow className="new-ticket-form-row">
            <Form.Item label={t('ticket_modal.driver')} name="driver">
              <span className={ticket.driver ? 'ticket-field-value' : 'ticket-field-value-error'}>
                {ticket.driver
                  ? `${ticket.driver.stateIdNumber} - ${ticket.driver.firstName} ${ticket.driver.lastName}`
                  : 'N/A'}
              </span>
            </Form.Item>
          </StyledRow>
          <StyledRow className="new-ticket-form-row">
            <Form.Item label={t('ticket_modal.truck')} name="truck">
              <span
                title={ticket.truck ? truckInfo : undefined}
                className={ticket.truck ? 'ticket-field-value' : 'ticket-field-value-error'}
              >
                {ticket.truck ? trimText(truckInfo, 50) : 'N/A'}
              </span>
            </Form.Item>
          </StyledRow>
          <StyledRow className="new-ticket-form-row">
            <Form.Item label={t('ticket_modal.trailer')} name="trailer">
              <span
                title={ticket?.trailer ? trailerInfo : undefined}
                className={ticket?.trailer ? 'ticket-field-value' : 'ticket-field-value-error'}
              >
                {ticket?.trailer ? trimText(trailerInfo, 50) : 'N/A'}
              </span>
            </Form.Item>
          </StyledRow>
          {ticket && declinedLoadEditionStatus.includes(ticket.status.name as StatusTicketEnum) && (
            <StyledRow
              className="new-ticket-form-row"
              style={{ display: 'inline-flex', alignItems: 'center' }}
            >
              <Form.Item label={t('ticket_modal.load_rejected')} name="declinedLoad">
                {roles.includes(ADMIN) ? (
                  <Checkbox
                    onChange={({ target }) => {
                      form.setFieldValue('declinedLoad', target.checked)
                      handlePrimaryButtonDisabling(false)
                    }}
                    checked={declinedLoadValue}
                  />
                ) : ticket.declinedLoadAt ? (
                  <span className="ticket-field-value">
                    {dayjs(ticket.declinedLoadAt).format(DATE_KEYS.FORMAT.FULL_TIME)}
                  </span>
                ) : (
                  <span className="ticket-field-value-error">N/A</span>
                )}
              </Form.Item>
            </StyledRow>
          )}
          {ticket &&
            declinedLoadValue &&
            declinedLoadEditionStatus.includes(ticket.status.name as StatusTicketEnum) && (
              <StyledRow className="new-ticket-form-row">
                <Form.Item
                  rules={[
                    {
                      required: true,
                      message: isRequiredMsg,
                    },
                  ]}
                  label={t('ticket_modal.reason_for_rejection')}
                  name="declinedLoadType"
                >
                  {roles.includes(ADMIN) ? (
                    <Select
                      onChange={(value: number) => {
                        form.setFieldValue('declinedLoadType', value)
                        handlePrimaryButtonDisabling(false)
                      }}
                      getPopupContainer={(triggerNode: HTMLElement) =>
                        triggerNode.parentNode as HTMLElement
                      }
                      placeholder={t('globals.placeholder_select')}
                      showSearch
                      filterOption={handleFilterOptions}
                      options={Object.values(DeclinedLoadTypeEnum)?.map(item => ({
                        label: t(`ticket_modal.${item.toLowerCase()}`),
                        value: item,
                      }))}
                      allowClear
                    />
                  ) : ticket.declinedLoadType ? (
                    <span className="ticket-field-value">
                      {t(`ticket_modal.${ticket.declinedLoadType.toLowerCase()}`)}
                    </span>
                  ) : (
                    <span className="ticket-field-value-error">N/A</span>
                  )}
                </Form.Item>
              </StyledRow>
            )}
          {/* REMITO — CFR has no carrier; still allow remito. FOB/others need carrier. */}
          {(isTripCfr || ticket.carrier?.id) && !isFutureTicket && (
            <>
              <StyledRow className="carrier-form-row">
                <FlexRow style={{ alignItems: 'start', gap: '8px', flex: 1 }}>
                  <Form.Item
                    label={
                      <DeliveryNoteLabel showNotFoundWarning={showDeliveryNoteNotFoundWarning}>
                        {t('suppliers_router.new_ticket_modal.delivery_note')}
                      </DeliveryNoteLabel>
                    }
                    name="number"
                    rules={[
                      {
                        required: false,
                        message: isRequiredMsg,
                      },
                      {
                        validator: (_, value) => {
                          const normalized = DeliveryNoteUtils.normalizeValue(value)
                          if (!normalized) return Promise.resolve()
                          if (
                            !DeliveryNoteUtils.isValidDeliveryNote(
                              normalized,
                              deliveryNoteMinLength,
                              deliveryNoteMaxLength,
                            )
                          ) {
                            return Promise.reject(new Error(deliveryNoteValidationMessage))
                          }
                          return Promise.resolve()
                        },
                      },
                    ]}
                  >
                    <Select
                      allowClear
                      showSearch
                      getPopupContainer={triggerNode => triggerNode.parentNode}
                      placeholder={t('globals.placeholder_select')}
                      options={deliveryNotesOptions}
                      loading={isDeliveryNotesLoading || isSyncingDeliveryNotes}
                      notFoundContent={t('suppliers_router.new_ticket_modal.no_delivery_notes')}
                      onChange={value => {
                        handlePrimaryButtonDisabling(false)
                        handleDeliveryNoteChange(value)
                        const input = document.activeElement as HTMLElement
                        if (input) input.blur()
                      }}
                      disabled={!isDeliveryNoteFieldEnabled}
                      // With OC: only select from unused remitos. Free typing allowed only without OC.
                      {...(currentDocumentNumberId ? {} : { mode: 'tags' as const, maxCount: 1 })}
                    />
                  </Form.Item>
                  <Tooltip title={t('suppliers_router.new_ticket_modal.refresh_delivery_notes')}>
                    <Button
                      type="primary"
                      icon={<SyncOutlined spin={isSyncingDeliveryNotes} />}
                      onClick={syncDeliveryNotes}
                      disabled={!isDeliveryNoteFieldEnabled || isSyncingDeliveryNotes}
                      loading={isSyncingDeliveryNotes}
                      className="contract-search-button"
                    />
                  </Tooltip>
                </FlexRow>
              </StyledRow>
              {/* TICKET SAP / Viaje GMS — optional for CFR; required only by backend rules for FOB/Transferencia */}
              <StyledRow className="carrier-form-row ticket-sap-row">
                <Form.Item
                  label={t('suppliers_router.new_ticket_modal.ticketSap')}
                  name="ticketSap"
                  hasFeedback
                  rules={[
                    {
                      required: false,
                      message: isRequiredMsg,
                    },
                    {
                      pattern: /^\d+$/,
                      message: t('message_ticket_sap_number'),
                    },
                  ]}
                >
                  <StyledInput
                    allowClear
                    disabled={isTicketReadOnly || !canEditRemitoAndTicketSap}
                    onChange={({ target }) => {
                      const value = target.value
                      // CFR must not block save when Viaje GMS is empty or being typed
                      if (isTripCfr) {
                        handlePrimaryButtonDisabling(false)
                        return
                      }
                      handlePrimaryButtonDisabling(value.length > 0 && !/^\d+$/.test(value))
                    }}
                  />
                </Form.Item>
              </StyledRow>
            </>
          )}
          {!ticket.carrier?.id && !isFutureTicket && (
            <>
              {ticket.deliveryNote && (
                <StyledRow className="new-ticket-form-row">
                  <Form.Item label={t('tpl_router.tpl_dashboard.delivery_note')} name="number">
                    <span title={ticket.ticketSap} className="ticket-field-value">
                      {ticket.deliveryNote}
                    </span>
                  </Form.Item>
                </StyledRow>
              )}
              {ticket.ticketSap && (
                <StyledRow className="new-ticket-form-row">
                  <Form.Item label={t('tpl_router.tpl_dashboard.ticketSap')} name="ticketSap">
                    <span title={ticket.ticketSap} className="ticket-field-value">
                      {ticket.ticketSap}
                    </span>
                  </Form.Item>
                </StyledRow>
              )}
            </>
          )}
        </FlexCol>
        {editable && (
          <FlexCol className="form-section">
            <StyledRow className="new-ticket-form-row comments">
              <Form.Item label={t('ticket_modal.comments')} name="comments">
                <TextArea maxLength={1200} rows={3} allowClear />
              </Form.Item>
            </StyledRow>
          </FlexCol>
        )}
        <FlexCol className="form-section flex-1">
          {editable && ticket?.status.name !== NO_SHOW ? (
            <TicketFilesVariantForm />
          ) : (
            <TicketFilesVariantDetail />
          )}
        </FlexCol>
        <FlexCol className="form-section form-comments">
          {!!ticket && (
            <StyledRow className="new-ticket-form-row">
              <Form.Item label={t('ticket_modal.comment')} className="form-comments-item">
                <div>
                  {ticket?.observations?.map((observation: Note) =>
                    !editable ? (
                      <CommentElement data={observation} />
                    ) : (
                      <NoteElement data={observation} />
                    ),
                  )}
                  {ticket?.status.name === CANCELED && ticket.deleteBy && ticket.canceledAt && (
                    <div className="form-deletion-info" style={{ marginTop: '1rem' }}>
                      <FlexRow style={{ gap: '4px', flexWrap: 'nowrap', marginBottom: '0.5rem' }}>
                        <FlexCol>
                          <Typography.Text strong>{t('ticket_modal.deleted_by')}</Typography.Text>
                        </FlexCol>
                        <FlexCol>
                          <span>
                            {ticket.deleteBy.userDetail?.name} {ticket.deleteBy.userDetail?.surname}{' '}
                            ({ticket.deleteBy.roles?.[0]?.name})
                          </span>
                        </FlexCol>
                      </FlexRow>
                      <FlexRow style={{ gap: '4px', flexWrap: 'nowrap' }}>
                        <FlexCol style={{ minWidth: '10rem', flexShrink: 0 }}>
                          <Typography.Text strong>
                            {t('ticket_modal.cancellation_date')}
                          </Typography.Text>
                        </FlexCol>
                        <FlexCol>
                          <span>{dayjs(ticket.canceledAt).format(DATE_KEYS.FORMAT.FULL_TIME)}</span>
                        </FlexCol>
                      </FlexRow>
                    </div>
                  )}
                </div>
              </Form.Item>
            </StyledRow>
          )}
        </FlexCol>
      </FlexCol>
      {/* TODO: Carriers table rendering here */}
      {showCarrierTable && (
        <FlexCol className="ticket-form__column carrier-selection">
          {carriersAvailabilitySummaryDataIsLoading ||
          !carriersAvailabilitySummaryData ||
          !equipments ? (
            <StyledSpinner />
          ) : (
            <>
              <FlexRow className="carrier-table-status-header">
                <Form.Item
                  rules={[
                    {
                      required:
                        (ticket?.status.name as StatusTicketEnum) === CREATED &&
                        assignValue === 'manual',
                      message: isRequiredMsg,
                    },
                  ]}
                  label={t('carrier_availability.ticket_modal.table_subtitle')}
                  name="carrierId"
                />
                <Tooltip
                  className="carrier-table-status__tooltip-refs"
                  color="white"
                  getPopupContainer={(triggerNode: HTMLElement) =>
                    triggerNode.parentNode as HTMLElement
                  }
                  placement="leftTop"
                  title={
                    <FlexCol className="carrier-table-status__tooltip-refs__title">
                      {statusRefs.map((status, index) => (
                        <FlexRow
                          key={status + '-' + index}
                          className="carrier-table-status__tooltip-refs__status-row"
                        >
                          <div
                            style={{
                              backgroundColor: (
                                theme.carrierTableColors as { [key: string]: string }
                              )[status],
                            }}
                            className="carrier-table-status-ref-sample"
                          />
                          <Typography className="carrier-table-status__tooltip-refs__status-row__description">
                            {t(`carrier_availability.ticket_modal.refs.${status}`)}
                          </Typography>
                        </FlexRow>
                      ))}
                    </FlexCol>
                  }
                >
                  <QuestionCircleOutlined className="refs-icon-info" />
                </Tooltip>
              </FlexRow>
              <Table
                scroll={{ y: '100%', x: '100%' }}
                className={classesToString([
                  'carrier-selection-table',
                  assignValue === 'automatic' &&
                    ticket?.status.name === CREATED &&
                    'disabled-input-opacity',
                ])}
                rowKey={({ carrier }) => carrier.id}
                sortDirections={['ascend', 'descend']}
                showSorterTooltip
                rowClassName={record => {
                  const ticketAssignmentStatuses = ticket?.ticketAssignments
                    .filter((item: TicketAssignmentType) => item.carrier.id === record.carrier.id)
                    .sort((a, b) => (a.id < b.id ? 1 : 0))
                  if (
                    !!ticketAssignmentStatuses?.length &&
                    ticketAssignmentStatuses[0].status.name === ticket?.status.name
                  ) {
                    return rowCarrierClassSetter(
                      ticketAssignmentStatuses[0].status.name as unknown as StatusTicketEnum,
                    )
                  }
                  return ''
                }}
                columns={[
                  {
                    title: t('carrier_availability.ticket_modal.name_column_header') as string,
                    dataIndex: 'carrier',
                    width: '11rem',
                    sorter: (a, b) => {
                      if (a.carrier.tradeName < b.carrier.tradeName) {
                        return -1
                      }
                      if (a.carrier.tradeName > b.carrier.tradeName) {
                        return 1
                      }
                      return 0
                    },
                    defaultSortOrder: 'ascend',
                    render: ({ tradeName }) => tradeName,
                  },
                  ...equipments
                    .filter(({ highDemand }) => !!highDemand)
                    .map(({ nameShort, id: equipmentId }) => ({
                      key: equipmentId,
                      title: () => (
                        <Flex
                          className={`carrier-column-header${
                            equipmentId === selectedEquipmentId
                              ? ' column-SELECTED_EQUIPMENT_COLUMN'
                              : ''
                          }`}
                        >
                          {nameShort}
                        </Flex>
                      ),
                      dataIndex: 'equipmentDataSummarized',
                      sorter: (a: CarrierAvailabilitySummary, b: CarrierAvailabilitySummary) =>
                        a.equipmentDataSummarized.find(
                          ({ equipment }: { equipment: Equipment }) => equipment.id === equipmentId,
                        )!.available -
                        b.equipmentDataSummarized.find(
                          ({ equipment }: { equipment: Equipment }) => equipment.id === equipmentId,
                        )!.available,
                      render: (equipmentDataSumarized: EquipmentDataSummarized[]) => (
                        <Flex
                          style={{
                            backgroundColor:
                              equipmentId === selectedEquipmentId
                                ? theme.carrierTableColors.SELECTED_EQUIPMENT_COLUMN
                                : '',
                            height: '100%',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <Typography.Text className="carrier-table-status-equipment-header">
                            {equipmentDataSumarized.find(
                              ({ equipment }: { equipment: Equipment }) =>
                                equipment.id === equipmentId,
                            )?.available ?? '-'}
                          </Typography.Text>
                        </Flex>
                      ),
                    })),
                ]}
                rowSelection={{
                  selectedRowKeys: selectedCarrier ? [selectedCarrier] : undefined,
                  type: 'radio',
                  renderCell:
                    (assignValue === 'automatic' && ticket?.status.name === CREATED) ||
                    ticketStatusToDisablePrimaryButtonforAdmin.includes(
                      ticket?.status.name as StatusTicketEnum,
                    )
                      ? () => <div />
                      : undefined,
                  hideSelectAll: true,
                  checkStrictly: true,
                  preserveSelectedRowKeys: true,
                  getCheckboxProps: () => ({
                    disabled: roles?.includes(ADMINRO),
                  }),
                  onChange: selectedRowKeys => {
                    if (roles?.includes(ADMIN)) {
                      clearSimulationOnCarrierChange()
                      setSelectedCarrier(selectedRowKeys[0] as unknown as number)
                    }
                  },
                  onSelect: record => {
                    clearSimulationOnCarrierChange()
                    const ticketAssignmentStatuses = ticket?.ticketAssignments
                      .filter((item: TicketAssignmentType) => item.carrier.id === record.carrier.id)
                      .sort((a, b) => (a.id < b.id ? 1 : 0))
                    if (
                      !!ticketAssignmentStatuses?.length &&
                      ticketAssignmentStatuses[0].status.name === ticket?.status.name
                    ) {
                      handleSearchContractPosition({
                        carrierId: record.carrier.id,
                        autoSelect: true,
                      })
                    } else {
                      clearContractFields()
                    }
                  },
                }}
                dataSource={carriersAvailabilitySummaryData}
                pagination={false}
              />
            </>
          )}
        </FlexCol>
      )}
    </Form>
  )
}

export default UpdateTicketFormAdmin
